import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '@environments/environment';
import { normalizeTimezone } from 'core/constants/timezone.constants';
import { AuthProvider, AuthSession } from 'core/models/auth-session.model';
import { ALL_ROLES, Role } from 'core/models/role.model';
import { User } from 'core/models/user.model';
import { AuthProfileApiResponse } from 'core/models/oneauth.model';
import { OneAuthService } from 'core/services/oneauth.service';
import { UserService } from 'core/services/user.service';

const STORAGE_KEY = 'sge.admin.auth.session';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly userService = inject(UserService);
  private readonly oneAuth = inject(OneAuthService);

  private readonly session = signal<AuthSession | null>(
    this.readStoredSession(),
  );

  readonly current = this.session.asReadonly();
  readonly isAuthenticated = computed(() => this.session() !== null);
  readonly user = computed(() => this.session()?.user ?? null);
  readonly role = computed(() => this.session()?.role ?? null);
  readonly institutionId = computed(() => this.session()?.institutionId);
  readonly useMockAuth = environment.useMockAuth;
  readonly canSwitchDemoUser = computed(
    () => environment.useMockAuth && this.session()?.provider === 'mock',
  );

  loginCandidates(): User[] {
    if (!environment.useMockAuth) {
      return [];
    }

    const active = this.userService.all().filter((user) => user.active);
    const byRole = new Map<Role, User>();

    for (const user of active) {
      if (!byRole.has(user.role)) {
        byRole.set(user.role, user);
      }
    }

    const current = this.user();
    if (current?.active) {
      byRole.set(current.role, current);
    }

    return ALL_ROLES.map((role) => byRole.get(role)).filter(
      (user): user is User => user !== undefined,
    );
  }

  loginAsUser(userId: string): AuthSession | null {
    if (!environment.useMockAuth) {
      return null;
    }

    const user = this.userService.getById(userId);
    if (!user || !user.active) {
      return null;
    }

    const next: AuthSession = {
      provider: 'mock',
      token: this.createFakeToken(user),
      user,
      role: user.role,
      institutionId: user.institutionId,
    };

    this.persist(next);
    return next;
  }

  switchUser(userId: string): AuthSession | null {
    return this.loginAsUser(userId);
  }

  syncCurrentUser(user: User): void {
    const current = this.session();
    if (!current || current.user.id !== user.id) {
      return;
    }

    const next: AuthSession = {
      ...current,
      user,
      role: user.role,
      institutionId: user.institutionId,
    };

    this.persist(next);
  }

  establishFromOneAuthProfile(
    token: string,
    profile: AuthProfileApiResponse['data'],
  ): AuthSession {
    if (
      profile.id === null ||
      profile.id === undefined ||
      String(profile.id).trim() === '' ||
      String(profile.id) === 'null'
    ) {
      throw new Error('Perfil OneAuth sem identificador público.');
    }

    const role = this.normalizeRole(profile.role);
    const user: User = {
      id: String(profile.id),
      name: profile.name,
      email: profile.email,
      role,
      institutionId:
        profile.institution_id !== null && profile.institution_id !== undefined
          ? String(profile.institution_id)
          : undefined,
      active: profile.active ?? true,
      hasSystemAccess: profile.has_system_access !== false,
      avatarUrl:
        typeof profile.avatar_url === 'string' &&
        profile.avatar_url.trim() !== ''
          ? profile.avatar_url.trim()
          : null,
      timezone: profile.timezone
        ? normalizeTimezone(profile.timezone)
        : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const next: AuthSession = {
      provider: 'oneauth',
      token,
      user,
      role,
      institutionId: user.institutionId,
      companySubdomain: profile.company_subdomain?.trim() || undefined,
      products: Array.isArray(profile.products)
        ? profile.products.map((item) => ({
            slug: item.slug ?? null,
            host: item.host ?? null,
          }))
        : undefined,
    };

    this.persist(next);
    return next;
  }

  logout(): void {
    const provider =
      this.session()?.provider ??
      (environment.useMockAuth ? 'mock' : 'oneauth');

    if (provider === 'oneauth' || !environment.useMockAuth) {
      // Não zera a sessão antes do redirect - evita flash do dash sem usuário.
      void this.oneAuth.logoutAndRedirectToSso();
      return;
    }

    this.session.set(null);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  startOneAuthLogin(intendedPath = '/', forcePrompt = false): void {
    this.oneAuth.redirectToLogin(intendedPath, forcePrompt);
  }

  hasRole(...roles: Role[]): boolean {
    const role = this.role();
    return role !== null && roles.includes(role);
  }

  /**
   * Reconsulta /auth/me para alinhar papel/sessao ao JWT atual.
   * Evita UI de admin com token de outro perfil (403 em /users).
   */
  revalidateFromApi(): Observable<AuthSession | null> {
    if (environment.useMockAuth || !this.isAuthenticated()) {
      return of(this.session());
    }

    const token = this.oneAuth.getToken();
    if (!token) {
      return of(this.session());
    }

    return this.oneAuth.fetchProfile(token).pipe(
      tap((response) => {
        if (response?.data) {
          this.establishFromOneAuthProfile(token, response.data);
        }
      }),
      map(() => this.session()),
      catchError(() => of(this.session())),
    );
  }

  private normalizeRole(raw: string): Role {
    const value = raw.trim().toUpperCase().replace(/-/g, '_');

    if ((ALL_ROLES as readonly string[]).includes(value)) {
      return value as Role;
    }

    return 'TEACHER';
  }

  private persist(session: AuthSession): void {
    this.session.set(session);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
  }

  private readStoredSession(): AuthSession | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as AuthSession;
      if (
        !parsed?.user?.id ||
        parsed.user.id === 'null' ||
        parsed.user.id === 'undefined' ||
        !parsed?.token
      ) {
        return null;
      }

      const provider: AuthProvider =
        parsed.provider ?? (environment.useMockAuth ? 'mock' : 'oneauth');

      if (environment.useMockAuth && provider === 'mock') {
        const user = this.userService.getById(parsed.user.id);
        if (!user || !user.active) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return {
          provider: 'mock',
          token: parsed.token,
          user,
          role: user.role,
          institutionId: user.institutionId,
        };
      }

      if (!environment.useMockAuth && provider === 'oneauth') {
        const token = this.oneAuth.getToken();
        if (!token) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return {
          provider: 'oneauth',
          token,
          user: parsed.user,
          role: this.normalizeRole(parsed.role ?? parsed.user.role),
          institutionId: parsed.institutionId ?? parsed.user.institutionId,
          companySubdomain: parsed.companySubdomain,
          products: parsed.products,
        };
      }

      localStorage.removeItem(STORAGE_KEY);
      return null;
    } catch {
      return null;
    }
  }

  private createFakeToken(user: User): string {
    const payload = btoa(
      JSON.stringify({
        sub: user.id,
        role: user.role,
        institutionId: user.institutionId ?? null,
        iat: Date.now(),
      }),
    );
    return `fake.${payload}.sig`;
  }
}
